import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash, Plugs, Plus, X, HardDrives, CaretDown, CaretRight, ArrowClockwise } from '@phosphor-icons/react';
import { CopyApiButton } from '../components/CopyApiButton';

const ForgeAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ label: '', api_token: '' });
  const [expanded, setExpanded] = useState(null);
  const [servers, setServers] = useState({}); // accId -> [servers]
  const [loadingServers, setLoadingServers] = useState(false);
  const [provisionFor, setProvisionFor] = useState(null); // accId
  const [credentials, setCredentials] = useState([]);
  const [pform, setPform] = useState({ provider: 'custom', name: '', ip_address: '', private_ip_address: '', credential_id: '', size: '', region: '', php_version: 'php83', ubuntu_version: '24.04' });
  const [provisionResult, setProvisionResult] = useState(null);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get('/api/forge/accounts', { withCredentials: true });
      setAccounts(res.data);
    } catch (e) { toast.error('Failed to fetch accounts'); }
  };

  useEffect(() => { fetchAccounts(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/forge/accounts', form, { withCredentials: true });
      toast.success('Forge account linked');
      setShowAdd(false);
      setForm({ label: '', api_token: '' });
      fetchAccounts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add account'); }
  };

  const removeAccount = async (id) => {
    if (!window.confirm('Remove this Forge account?')) return;
    try { await axios.delete(`/api/forge/accounts/${id}`, { withCredentials: true }); fetchAccounts(); toast.success('Removed'); }
    catch (e) { toast.error('Failed to remove'); }
  };

  const testConnection = async (id) => {
    try { const res = await axios.post(`/api/forge/accounts/${id}/test`, {}, { withCredentials: true }); toast.success(`Connected as ${res.data.email || res.data.name}`); }
    catch (e) { toast.error('Connection failed: invalid token'); }
  };

  const loadServers = async (id) => {
    setLoadingServers(true);
    try {
      const res = await axios.get(`/api/forge/accounts/${id}/servers`, { withCredentials: true });
      setServers(prev => ({ ...prev, [id]: res.data }));
    } catch (e) { toast.error('Failed to load servers'); }
    setLoadingServers(false);
  };

  const toggleExpand = (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!servers[id]) loadServers(id);
  };

  const openProvision = async (id) => {
    setProvisionFor(id);
    setProvisionResult(null);
    setPform({ provider: 'custom', name: '', ip_address: '', private_ip_address: '', credential_id: '', size: '', region: '', php_version: 'php83', ubuntu_version: '24.04' });
    try {
      const res = await axios.get(`/api/forge/accounts/${id}/credentials`, { withCredentials: true });
      setCredentials(res.data || []);
    } catch (e) { setCredentials([]); }
  };

  const submitProvision = async (e) => {
    e.preventDefault();
    const payload = { provider: pform.provider, name: pform.name, type: 'app', php_version: pform.php_version, ubuntu_version: pform.ubuntu_version };
    if (pform.provider === 'custom') {
      if (!pform.ip_address) return toast.error('IP address required for custom VPS');
      payload.ip_address = pform.ip_address;
      if (pform.private_ip_address) payload.private_ip_address = pform.private_ip_address;
    } else {
      if (!pform.credential_id) return toast.error('Select a cloud credential');
      payload.credential_id = parseInt(pform.credential_id);
      payload.size = pform.size;
      payload.region = pform.region;
    }
    try {
      const res = await axios.post(`/api/forge/accounts/${provisionFor}/servers`, payload, { withCredentials: true });
      toast.success('Server provisioning started (takes ~10 min).');
      setProvisionResult(res.data);
      loadServers(provisionFor);
    } catch (e) { toast.error(e.response?.data?.detail || 'Provision failed'); }
  };

  const deleteServer = async (accId, serverId) => {
    if (!window.confirm('Delete this server on Forge? This is permanent.')) return;
    try { await axios.delete(`/api/forge/accounts/${accId}/servers/${serverId}`, { withCredentials: true }); toast.success('Server deletion started'); loadServers(accId); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-black text-[#FB503B]">FORGE ACCOUNTS</h1>
        <button onClick={() => setShowAdd(true)} data-testid="forge-add-account-btn" className="flex items-center gap-2 bg-[#FB503B] text-white px-4 py-2 font-bold hover:bg-[#e0402d] transition-colors">
          <Plus size={20} /> Add Account
        </button>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-white/20 p-8 w-full max-w-lg relative animate-in zoom-in-95 duration-200">
            <button onClick={() => setShowAdd(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X size={24} /></button>
            <h2 className="text-2xl font-bold mb-2 text-[#FB503B]">LINK FORGE ACCOUNT</h2>
            <p className="text-xs text-slate-500 mb-6 font-mono">Get a token at Forge → Account → API → Create token.</p>
            <form onSubmit={handleAdd} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Label / Nickname</label>
                <input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="forge-account-label" className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#FB503B]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Forge API Token</label>
                <input required type="password" value={form.api_token} onChange={(e) => setForm({ ...form, api_token: e.target.value })} data-testid="forge-account-token" className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#FB503B]" />
              </div>
              <button type="submit" data-testid="forge-account-submit" className="w-full bg-[#FB503B] text-white font-bold py-3 mt-4 hover:bg-[#e0402d] transition-colors">LINK ACCOUNT</button>
            </form>
          </div>
        </div>
      )}

      {provisionFor && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-white/20 p-8 w-full max-w-lg relative animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setProvisionFor(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X size={24} /></button>
            <h2 className="text-2xl font-bold mb-2 text-[#FB503B]">PROVISION SERVER</h2>
            <p className="text-xs text-slate-500 mb-6 font-mono">Sites live on a server. Use a Custom VPS (existing server IP, no cloud credential) or a connected cloud provider.</p>
            {provisionResult ? (
              <div className="space-y-4">
                <p className="text-[#39FF14] font-bold">Provisioning started.</p>
                {provisionResult.provision_command && (
                  <div>
                    <p className="text-xs uppercase text-slate-500 font-bold mb-1">Run this on your custom VPS:</p>
                    <textarea readOnly value={provisionResult.provision_command} className="w-full h-28 bg-[#0A0B0E] border border-white/10 p-2 font-mono text-xs select-all" />
                  </div>
                )}
                <button onClick={() => setProvisionFor(null)} className="w-full bg-[#FB503B] text-white font-bold py-3 hover:bg-[#e0402d] transition-colors">DONE</button>
              </div>
            ) : (
              <form onSubmit={submitProvision} className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Provider</label>
                  <select value={pform.provider} onChange={(e) => setPform({ ...pform, provider: e.target.value })} data-testid="forge-provision-provider" className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#FB503B]">
                    <option value="custom">Custom VPS (existing server)</option>
                    <option value="ocean2">DigitalOcean</option>
                    <option value="hetzner">Hetzner</option>
                    <option value="vultr2">Vultr</option>
                    <option value="akamai">Akamai / Linode</option>
                    <option value="aws">AWS</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Server Name</label>
                  <input required value={pform.name} onChange={(e) => setPform({ ...pform, name: e.target.value })} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#FB503B]" />
                </div>
                {pform.provider === 'custom' ? (
                  <>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Public IP Address</label>
                      <input required value={pform.ip_address} onChange={(e) => setPform({ ...pform, ip_address: e.target.value })} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 font-mono focus:outline-none focus:border-[#FB503B]" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Private IP (optional)</label>
                      <input value={pform.private_ip_address} onChange={(e) => setPform({ ...pform, private_ip_address: e.target.value })} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 font-mono focus:outline-none focus:border-[#FB503B]" />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Cloud Credential</label>
                      <select value={pform.credential_id} onChange={(e) => setPform({ ...pform, credential_id: e.target.value })} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#FB503B]">
                        <option value="">-- select credential --</option>
                        {credentials.map(c => <option key={c.id} value={c.id}>{c.name} ({c.type})</option>)}
                      </select>
                      {credentials.length === 0 && <p className="text-[10px] text-yellow-500 mt-1">No cloud credentials connected in Forge. Connect one in Forge, or use Custom VPS.</p>}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Size (slug)</label>
                        <input value={pform.size} onChange={(e) => setPform({ ...pform, size: e.target.value })} placeholder="e.g. 01 / s-1vcpu-1gb" className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 font-mono focus:outline-none focus:border-[#FB503B]" />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Region</label>
                        <input value={pform.region} onChange={(e) => setPform({ ...pform, region: e.target.value })} placeholder="e.g. nyc3" className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 font-mono focus:outline-none focus:border-[#FB503B]" />
                      </div>
                    </div>
                  </>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">PHP</label>
                    <select value={pform.php_version} onChange={(e) => setPform({ ...pform, php_version: e.target.value })} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#FB503B]">
                      <option value="php83">PHP 8.3</option>
                      <option value="php82">PHP 8.2</option>
                      <option value="php81">PHP 8.1</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Ubuntu</label>
                    <select value={pform.ubuntu_version} onChange={(e) => setPform({ ...pform, ubuntu_version: e.target.value })} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#FB503B]">
                      <option value="24.04">24.04</option>
                      <option value="22.04">22.04</option>
                    </select>
                  </div>
                </div>
                <button type="submit" data-testid="forge-provision-submit" className="w-full bg-[#FB503B] text-white font-bold py-3 mt-2 hover:bg-[#e0402d] transition-colors">PROVISION SERVER</button>
              </form>
            )}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {accounts.map(acc => (
          <div key={acc.id} className="border border-[#FB503B]/20 bg-card">
            <div className="p-6 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <button onClick={() => toggleExpand(acc.id)} data-testid={`forge-expand-${acc.id}`} className="text-slate-400 hover:text-white">
                  {expanded === acc.id ? <CaretDown size={18} /> : <CaretRight size={18} />}
                </button>
                <div>
                  <h3 className="text-xl font-bold">{acc.label}</h3>
                  <p className="text-xs font-mono text-slate-500">{acc.forge_email} · Token {acc.api_token}</p>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <button onClick={() => { setExpanded(acc.id); if (!servers[acc.id]) loadServers(acc.id); openProvision(acc.id); }} data-testid={`forge-create-server-${acc.id}`} className="flex items-center gap-1 bg-[#FB503B] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-[#e0402d] transition-colors" title="Provision a new server">
                  <HardDrives size={16} /> Create Server
                </button>
                <button onClick={() => testConnection(acc.id)} className="text-slate-400 hover:text-[#FB503B] transition-colors p-1" title="Test Connection"><Plugs size={20} /></button>
                <CopyApiButton provider="forge" accountId={acc.id} />
                <button onClick={() => removeAccount(acc.id)} className="text-slate-400 hover:text-red-500 transition-colors p-1" title="Remove Account"><Trash size={20} /></button>
              </div>
            </div>

            {expanded === acc.id && (
              <div className="border-t border-white/10 p-6 bg-black/20 space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2"><HardDrives size={16} /> Servers</h4>
                  <div className="flex gap-2">
                    <button onClick={() => loadServers(acc.id)} className="text-slate-400 hover:text-white p-1" title="Refresh"><ArrowClockwise size={16} /></button>
                    <button onClick={() => openProvision(acc.id)} data-testid={`forge-provision-btn-${acc.id}`} className="text-xs bg-[#FB503B] text-white px-3 py-1 font-bold hover:bg-[#e0402d] transition-colors flex items-center gap-1"><Plus size={14} /> Provision</button>
                  </div>
                </div>
                {loadingServers ? <p className="text-slate-500 text-sm">Loading…</p> : (
                  <div className="space-y-2">
                    {(servers[acc.id] || []).map(s => (
                      <div key={s.id} className="flex justify-between items-center bg-[#0A0B0E] border border-white/10 p-3 font-mono text-sm">
                        <div>
                          <span className="text-white font-bold">{s.name}</span>
                          <span className="text-slate-500 ml-3">{s.ip_address || 'provisioning…'}</span>
                          <span className={`ml-3 text-xs ${s.is_ready ? 'text-[#39FF14]' : 'text-yellow-400'}`}>{s.is_ready ? 'ready' : 'not ready'}</span>
                        </div>
                        <button onClick={() => deleteServer(acc.id, s.id)} className="text-slate-500 hover:text-red-500"><Trash size={16} /></button>
                      </div>
                    ))}
                    {(servers[acc.id] || []).length === 0 && <p className="text-slate-500 text-sm">No servers yet. Provision one to host your on-forge.com sites.</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {accounts.length === 0 && (
          <div className="p-8 border border-dashed border-white/20 text-center text-slate-500">No Forge accounts linked.</div>
        )}
      </div>
    </div>
  );
};

export default ForgeAccounts;
